import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Sparkles, Clock, ShieldX, LogOut, Mail } from "lucide-react";

function CenterCard({ icon: Icon, tone, title, children }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center">
      <span className={`flex h-16 w-16 items-center justify-center rounded-full ${tone}`}>
        <Icon className="h-8 w-8" />
      </span>
      <h1 className="mt-6 font-serif text-3xl font-semibold">{title}</h1>
      <div className="mt-3 max-w-md text-muted-foreground">{children}</div>
      {user && (
        <div className="mt-6 flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
          <Mail className="h-4 w-4 text-gold" /> {user.email}
        </div>
      )}
      <button onClick={logout} data-testid="status-logout-btn" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );
}

export default function ProtectedRoute({ children, perm }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary">
          <Sparkles className="h-5 w-5 text-gold" />
        </span>
        <div className="mt-6 h-6 w-6 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (user.status === "rejected") {
    return (
      <CenterCard icon={ShieldX} tone="bg-red-100 text-red-700" title="Access not granted">
        Your request to join the Godinga team workspace was declined. If you believe this is a mistake, please contact your administrator.
      </CenterCard>
    );
  }

  if (user.status === "suspended") {
    return (
      <CenterCard icon={ShieldX} tone="bg-zinc-200 text-zinc-700" title="Access suspended">
        Your access to the Godinga team workspace has been suspended. Please contact your administrator.
      </CenterCard>
    );
  }

  if (user.status !== "approved") {
    return (
      <CenterCard icon={Clock} tone="bg-amber-100 text-amber-700" title="Awaiting approval">
        Thanks for signing in! Your account is pending approval from a Godinga administrator. You'll gain access as soon as you're approved.
      </CenterCard>
    );
  }

  if (perm && !can(user, perm)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
